import { flow } from 'fp-ts/function';
import { array } from 'fp-ts';
import { FormInputField, LocalizedText, useFeatureFlag } from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { DateField } from '@mews-commander/form';
import { LockableField } from '../../LockableField';
import { FieldName, WithFieldsConfig } from '../types';
import { EnterprisesSelectField } from '../../../../../../enterprises';
import { WithEnterpriseIds, WithVoucherIds } from '../../../../../types';
import { RateKeysField } from './RateKeysField';
import { CompanyKeySelectField } from './CompanyKeySelectField';

type VoucherFormFieldsProps = WithFieldsConfig &
    WithVoucherIds &
    WithEnterpriseIds & {
        unlockField?: (fieldName: FieldName) => void;
        voucherCount?: number;
        isCreatingFromRates?: boolean;
    };

export const VoucherFormFields = ({
    fieldsConfig,
    voucherCount,
    enterpriseIds,
    voucherIds,
    unlockField,
    isCreatingFromRates,
}: VoucherFormFieldsProps) => {
    const isNameFieldLocked = fieldsConfig?.lockedFields?.includes('name') || false;
    const isPropertiesFieldLocked = fieldsConfig?.lockedFields?.includes('properties') || false;
    const isKeyFieldDisabled = fieldsConfig?.disabledFields?.includes('key') || false;
    const isStartUtcFieldLocked = fieldsConfig?.lockedFields?.includes('occupiableIntervalStartUtc') || false;
    const isEndUtcFieldLocked = fieldsConfig?.lockedFields?.includes('occupiableIntervalEndUtc') || false;
    const isCompanyKeyLocked = fieldsConfig?.lockedFields?.includes('companyKey') || false;

    const isEnterprisesSelectFieldEnabled = useFeatureFlag(FeatureFlag.enterprisesSelectFieldEnabled);

    const voucherKeyHelperText =
        (voucherCount && voucherCount > 1) || enterpriseIds.length > 1
            ? TextKey.VoucherBulkEditVouchersKeyHint
            : TextKey.VoucherBulkEditSingleVoucherKeyHint;

    const isCreateForm = !voucherIds || array.isEmpty(voucherIds);
    const getUnlockFunction = (fieldName: FieldName) => () => (unlockField ? unlockField(fieldName) : null);

    return (
        <>
            <LockableField
                locked={isNameFieldLocked}
                lockedReasonText={TextKey.VouchersValuesAreDifferent}
                tooltipTextKey={TextKey.UnlockableVouchersField}
                fieldName="name"
                onUnlock={getUnlockFunction('name')}
            >
                {(isLocked) => (
                    <FormInputField
                        required
                        name="name"
                        disabled={isLocked}
                        label={<LocalizedText textKey={TextKey.VoucherName} />}
                    />
                )}
            </LockableField>

            {isEnterprisesSelectFieldEnabled && isCreateForm ? (
                <LockableField
                    locked={isPropertiesFieldLocked}
                    lockedReasonText={TextKey.VouchersValuesAreDifferent}
                    tooltipTextKey={TextKey.UnlockableVouchersField}
                    fieldName="properties"
                    onUnlock={getUnlockFunction('properties')}
                >
                    {(isLocked) => (
                        <EnterprisesSelectField
                            name="properties"
                            disabled={isLocked}
                            label={<LocalizedText textKey={TextKey.Properties} />}
                            multiselect
                            compact
                        />
                    )}
                </LockableField>
            ) : null}

            <FormInputField
                name="key"
                disabled={isKeyFieldDisabled}
                label={<LocalizedText textKey={TextKey.VoucherKey} />}
                captionIconWithTooltip={
                    <TooltippedIcon size="small" value={IconName.Info} tooltip={voucherKeyHelperText} />
                }
                helper={
                    isKeyFieldDisabled ? <LocalizedText textKey={TextKey.SomeVouchersAreFromSameEnterprise} /> : null
                }
            />

            <LockableField
                locked={isCompanyKeyLocked}
                lockedReasonText={TextKey.VouchersValuesAreDifferent}
                tooltipTextKey={TextKey.UnlockableVouchersField}
                fieldName="companyKey"
                onUnlock={getUnlockFunction('companyKey')}
            >
                {(isLocked) => <CompanyKeySelectField disabled={isLocked} enterpriseIds={enterpriseIds} />}
            </LockableField>

            <LockableField
                locked={isStartUtcFieldLocked}
                lockedReasonText={TextKey.VouchersValuesAreDifferent}
                tooltipTextKey={TextKey.UnlockableVouchersField}
                fieldName="occupiableIntervalStartUtc"
                onUnlock={flow(
                    getUnlockFunction('occupiableIntervalStart'),
                    getUnlockFunction('occupiableIntervalStartUtc')
                )}
            >
                {(isDisabled) => (
                    <DateField
                        mode="localized"
                        name="occupiableIntervalStartUtc"
                        disabled={isDisabled}
                        label={<LocalizedText textKey={TextKey.ForReservationsFrom} />}
                    />
                )}
            </LockableField>
            <LockableField
                locked={isEndUtcFieldLocked}
                lockedReasonText={TextKey.VouchersValuesAreDifferent}
                tooltipTextKey={TextKey.UnlockableVouchersField}
                fieldName="occupiableIntervalEndUtc"
                onUnlock={flow(
                    getUnlockFunction('occupiableIntervalEnd'),
                    getUnlockFunction('occupiableIntervalEndUtc')
                )}
            >
                {(isDisabled) => (
                    <DateField
                        mode="localized"
                        name="occupiableIntervalEndUtc"
                        disabled={isDisabled}
                        label={<LocalizedText textKey={TextKey.ForReservationsTo} />}
                    />
                )}
            </LockableField>
            {!isCreatingFromRates && (
                <RateKeysField
                    enterpriseIds={enterpriseIds}
                    voucherIds={voucherIds}
                    fieldsConfig={fieldsConfig}
                    required={isCreateForm}
                    onUnlock={getUnlockFunction('rateKeys')}
                />
            )}
        </>
    );
};
